import type { Jss, StyleSheet } from 'jss';
import { create } from 'jss';
import jssPluginSyntaxCamelCase from 'jss-plugin-camel-case';
import jssPluginSyntaxDefaultUnit from 'jss-plugin-default-unit';
import jssPluginSyntaxNested from 'jss-plugin-nested';
import jssPluginSyntaxRuleValueFunction from 'jss-plugin-rule-value-function';

import { AdSlot } from '../../entities/ad-slot';
import { randomString } from '../../utils/string';
import { Inject, Injectable } from '../dependency-injection/decorators';
import { StylesheetProvider } from '../provider/stylesheet.provider';

@Injectable()
export class StylesheetRenderer {
  private readonly jssInstance: Jss;
  private readonly stylesheets: Map<string, StyleSheet>;
  private static CLASS_NAMES_LENGTH = 10;

  public constructor (
    private readonly stylesheetProvider: StylesheetProvider,
    @Inject('kernel.debug') private readonly debug: boolean,
  ) {
    this.stylesheets = new Map();
    this.jssInstance = create({
      id: { minify: !this.debug },
      createGenerateId: (options) => (rule): string => {
        const random = randomString(StylesheetRenderer.CLASS_NAMES_LENGTH);

        return options?.minify ? `c${random}` : `${rule.key}-${random}`;
      },
      plugins: [
        // order is important
        jssPluginSyntaxRuleValueFunction(),
        // jssPluginSyntaxExtend(),
        jssPluginSyntaxNested(),
        jssPluginSyntaxCamelCase(),
        jssPluginSyntaxDefaultUnit(),
      ],
    });
  }

  public async render (adSlot: AdSlot): Promise<void> {
    const adSlotId = adSlot?.getId();

    if (!adSlotId) {
      return;
    }

    const stylesheet = this.getStylesheet(adSlot);

    if (stylesheet.attached) {
      return;
    }

    stylesheet.update({
      format: adSlot.getSettings().getFormat(),
      style: adSlot.getSettings().getStyle(),
      creativeType: adSlot.getCreativeType(),
    });
    stylesheet.attach();
  }

  public getClasses (adSlot: AdSlot): Record<string, string> {
    return this.getStylesheet(adSlot).classes || {};
  }

  public getClass (adSlot: AdSlot, className: string): string {
    return this.getClasses(adSlot)[ className ] ?? '';
  }

  public getStylesheet (adSlot: AdSlot): StyleSheet {
    const key = adSlot.getId();
    const stylesheet = this.stylesheets.get(key)
      ?? this.jssInstance.createStyleSheet(this.stylesheetProvider.provide(adSlot));

    this.stylesheets.set(key, stylesheet);

    return stylesheet;
  }
}
